import { Container } from '@seek/ai-elements';
import { useTranslations } from '@vocab/react';
import { Box, Button, IconAI, Stack } from 'braid-design-system';
import { useCallback } from 'react';

import { CareerFeedAIAssistantContentFrame } from '../CareerFeedAIAssistantContent/CareerFeedAIAssistantContentFrame';
import { PrivacyAndCollectionNotice } from '../CareerFeedAIAssistantContent/PrivacyAndCollectionNotice';
import { CareerFeedAIHeading } from '../CareerFeedAIHeading';
import { CareerFeedMobileWebFeatureFlagContainer } from '../CareerFeedMobileWebFeatureFlagContainer';
import { useCareerFeedAIAssistant } from '../hooks/useCareerFeedAIAssistant';
import { useRecommendationForCareerFeedAiAssistant } from '../hooks/useRecommendationForCareerFeedAiAssistant';
import type { ErrorType } from '../types';

import translations from './.vocab';
import SocialProofSection from './SocialProofSection/SocialProofSection';

const CareerFeedAiAssistantInitialStateView = ({
  isButtonLoading,
  errorType,
  onButtonClick,
}: {
  isButtonLoading: boolean;
  errorType?: ErrorType | null;
  onButtonClick: () => void;
}) => {
  const { t } = useTranslations(translations);

  return (
    <CareerFeedAIAssistantContentFrame
      headMessage={{
        text: t(
          'Answer a few questions so we can find the right jobs for you.',
        ),
        size: 'standard',
      }}
      errorType={errorType}
      socialProofSection={<SocialProofSection />}
      ctaSection={
        <Stack space="small">
          <Box maxWidth="content">
            <Button
              tone="brandAccent"
              icon={<IconAI />}
              loading={isButtonLoading}
              onClick={onButtonClick}
            >
              {t('Refine recommendations')}
            </Button>
          </Box>
          <PrivacyAndCollectionNotice />
        </Stack>
      }
      loading={isButtonLoading}
    />
  );
};

export const CareerFeedAiAssistantInitialState = () => {
  const {
    invokeCreateSession,
    isCreateSessionLoading,
    createSessionError,
    tracking: { trackGenAIChatStartPressed },
  } = useCareerFeedAIAssistant();
  const { jobIds: recommendedJobIds } =
    useRecommendationForCareerFeedAiAssistant();

  const { t } = useTranslations(translations);

  const startSession = useCallback(() => {
    if (!isCreateSessionLoading) {
      trackGenAIChatStartPressed();
      invokeCreateSession(recommendedJobIds);
    }
  }, [
    invokeCreateSession,
    isCreateSessionLoading,
    recommendedJobIds,
    trackGenAIChatStartPressed,
  ]);

  return (
    <CareerFeedMobileWebFeatureFlagContainer
      mobileView={
        <Container borderWidth="standard">
          <Stack space="medium">
            <CareerFeedAIHeading headingText={t('Want better job matches?')} />
            <CareerFeedAiAssistantInitialStateView
              isButtonLoading={isCreateSessionLoading}
              errorType={createSessionError}
              onButtonClick={startSession}
            />
          </Stack>
        </Container>
      }
      tabletAndAboveView={
        <CareerFeedAiAssistantInitialStateView
          isButtonLoading={isCreateSessionLoading}
          errorType={createSessionError}
          onButtonClick={startSession}
        />
      }
    />
  );
};
